import * as teams from "@microsoft/teams-js";
import { useEffect, useMemo, useState } from "react";

/**
 * The type of Teams meeting currently active in the side panel.
 * - `'scheduled'`: a meeting with a join URL (scheduled, recurring, or webinar)
 * - `'adhoc'`: an ad-hoc call with no persistent join URL (Meet Now, group call, 1:1 call)
 * - `undefined`: not in a meeting / still loading
 */
export type TeamsMeetingType = "scheduled" | "adhoc" | undefined;

export const useTeamsContext = () => {
  const [teamsContext, setTeamsContext] = useState<
    teams.app.Context | undefined
  >();

  useEffect(() => {
    const getTeamsContext = async () => {
      const context = await teams.app.getContext();
      setTeamsContext(context);
    };
    getTeamsContext();
  }, []);

  return teamsContext;
};

export const useInMeeting = () => {
  const teamsContext = useTeamsContext();
  return useMemo(() => {
    const inMeeting =
      !!teamsContext &&
      teamsContext.page.frameContext === teams.FrameContexts.sidePanel;
    return inMeeting;
  }, [teamsContext]);
};

export function useIsInTeams() {
  const [isInTeams, setIsInTeams] = useState<boolean | null>(null);

  useEffect(() => {
    // This must be wrapped in a try/catch because initialization can throw if not in Teams
    const detectTeams = async () => {
      try {
        await teams.app.initialize();
        setIsInTeams(true);
      } catch {
        setIsInTeams(false);
      }
    };
    detectTeams();
  }, []);

  return isInTeams;
}

export function useIsInTeamsMobile() {
  const teamsContext = useTeamsContext();
  return useMemo(() => {
    return (
      teamsContext?.app.host.clientType === teams.HostClientType.ios ||
      teamsContext?.app.host.clientType === teams.HostClientType.android
    );
  }, [teamsContext]);
}

/**
 * Returns the type of the current Teams meeting when viewing the side panel.
 *
 * - `'scheduled'`: the meeting has a persistent join URL (scheduled, recurring, or webinar).
 *   In this flow the bot can be invited via that URL.
 * - `'adhoc'`: the meeting is an ad-hoc call (group call, or 1:1 call) with no
 *   persistent join URL.
 * - `undefined`: not in a meeting side panel, or context is still loading.
 *
 * Detection strategy: scheduled/recurring/broadcast meetings have a meeting chat thread
 * whose ID contains the "meeting_" prefix (e.g. "19:meeting_XXX@thread.v2").
 * Ad-hoc group calls and 1:1 calls reuse an existing chat thread without that prefix.
 * This avoids the `getMeetingDetailsVerbose` internal API which requires additional
 * RSC manifest permissions.
 */
export function useTeamsMeetingType(): TeamsMeetingType {
  const teamsContext = useTeamsContext();

  return useMemo(() => {
    if (!teamsContext) return undefined;
    if (teamsContext.page.frameContext !== teams.FrameContexts.sidePanel)
      return undefined;

    const chatId = teamsContext.chat?.id ?? "";
    return chatId.includes("meeting_") ? "scheduled" : "adhoc";
  }, [teamsContext]);
}
